import { CachedLearningAudio } from '@shared/media/audio/CachedLearningAudio'
import { type CSSProperties, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Mic, Pause, Play, Square, Volume2 } from 'lucide-react'
import type {
  LearningContentItem,
  LearningConversationItem,
} from '@services/firebase/learning'
import {
  resolveLocalizedMapText,
  resolveTranslatedText,
} from '@features/learning/shared/learning-helpers'
import { useAudioRecorder } from '@shared/media/recording/useAudioRecorder'
import { LmsButton } from '@shared/ui'
import { useTranslation } from 'react-i18next'
import type { SelfStudyContinueResult } from '../SelfStudyStepRenderer'
import { shuffleItems } from './answer-helpers'
import { LegacySpeechRecoveryNotice } from './speech-recovery-notice'
import { LearningTemplateResultBar as LegacyTemplateResultBar } from '@features/learning/shared/templates'
import '../self-study-native.css'

interface ConversationFillSlot {
  readonly conversationIndex: number
  readonly expected: string
  readonly key: string
  readonly sort: number
  readonly wordIndex: number
}

interface ConversationFillChoice {
  readonly id: string
  readonly text: string
}

function getConversationText(item: LearningConversationItem) {
  return item.text ?? item.answer ?? ''
}

function getConversationTranslation(item: LearningConversationItem, locale: string) {
  return (
    resolveTranslatedText(item.translates, locale) ??
    resolveLocalizedMapText(item.translate, locale)
  )
}

function LegacyConversationTurn({
  active,
  conversation,
  fillAnswers = {},
  fillResult = null,
  fillSlots = [],
  isSpeakingTurn = false,
  locale,
  onAudioFinished,
  onBeforePlayback,
  onFillDrop,
  onFillReselect,
  showTranslation,
  turnIndex,
}: {
  readonly active?: boolean
  readonly conversation: LearningConversationItem
  readonly fillAnswers?: Readonly<Record<string, string>>
  readonly fillResult?: 'correct' | 'incorrect' | null
  readonly fillSlots?: readonly ConversationFillSlot[]
  readonly isSpeakingTurn?: boolean
  readonly locale: string
  readonly onAudioFinished?: () => void
  readonly onBeforePlayback?: (turnIndex: number) => void
  readonly onFillDrop?: (slot: ConversationFillSlot, choiceId: string) => void
  readonly onFillReselect?: (slot: ConversationFillSlot) => void
  readonly showTranslation: boolean
  readonly turnIndex: number
}) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const autoStartedRef = useRef(false)
  const [isPlaying, setIsPlaying] = useState(false)
  const text = getConversationText(conversation)
  const translation = getConversationTranslation(conversation, locale)
  const isRight = Boolean(conversation.isRight)
  const name = conversation.name ?? conversation.speaker ?? (isRight ? 'B' : 'A')

  useEffect(() => {
    autoStartedRef.current = false
  }, [active, conversation.audio])

  useEffect(() => {
    if (!active) {
      audioRef.current?.pause()
      return undefined
    }

    if (autoStartedRef.current) {
      return undefined
    }

    if (!conversation.audio) {
      autoStartedRef.current = true
      onBeforePlayback?.(turnIndex)
      const timeout = window.setTimeout(() => {
        onAudioFinished?.()
      }, 500)
      return () => window.clearTimeout(timeout)
    }

    const audio = audioRef.current
    if (!audio || audio.readyState < HTMLMediaElement.HAVE_CURRENT_DATA) {
      return undefined
    }

    autoStartedRef.current = true
    onBeforePlayback?.(turnIndex)
    void audio.play().catch(() => {
      setIsPlaying(false)
    })
    return undefined
  }, [active, conversation.audio, onAudioFinished, onBeforePlayback, turnIndex])

  const togglePlayback = () => {
    const audio = audioRef.current
    if (!audio) {
      return
    }

    onBeforePlayback?.(turnIndex)
    if (audio.paused) {
      void audio.play()
    } else {
      audio.pause()
    }
  }

  const renderText = () => {
    if (showTranslation && translation && translation !== text) {
      return translation
    }

    if (fillSlots.length === 0) {
      return text
    }

    const words = text.split(' ')

    return words.map((word, wordIndex) => {
      const slot = fillSlots.find((candidate) => candidate.wordIndex === wordIndex)
      if (!slot) {
        return `${word}${wordIndex === words.length - 1 ? '' : ' '}`
      }

      return (
        <LmsButton
          className={`legacySelfStudyConversationFillBlank${
            fillAnswers[slot.key] ? ' filled' : ''
          }${fillResult && fillAnswers[slot.key] ? ` ${fillResult}` : ''}`}
          key={slot.key}
          onClick={(event) => {
            event.stopPropagation()
            onFillReselect?.(slot)
          }}
          onDragOver={(event) => {
            if (fillResult) {
              return
            }
            event.preventDefault()
            event.dataTransfer.dropEffect = 'move'
          }}
          onDrop={(event) => {
            if (fillResult) {
              return
            }
            event.preventDefault()
            event.stopPropagation()
            const choiceId =
              event.dataTransfer.getData('application/x-hanaspeak-fill-choice') ||
              event.dataTransfer.getData('text/plain').replace(/^conversation-fill-choice:/, '')
            if (choiceId) {
              onFillDrop?.(slot, choiceId)
            }
          }}
          type="button"
          variant="ghost"
        >
          {fillAnswers[slot.key] ?? ''}
          <span className="legacySelfStudyConversationSort">{slot.sort}</span>
        </LmsButton>
      )
    })
  }

  return (
    <div
      className={`legacySelfStudyConversationTurn ${isRight ? 'right' : 'left'}${
        isSpeakingTurn ? ' speaking' : ''
      }`}
      id={`conversationBox-${turnIndex}`}
    >
      <LmsButton
        aria-label={`Play ${name}'s audio`}
        className="legacySelfStudyConversationAvatar"
        onClick={togglePlayback}
        size="icon"
        type="button"
        variant="ghost"
      >
        {conversation.image ?? conversation.avatar ? (
          <img alt="" src={conversation.image ?? conversation.avatar} />
        ) : null}
        {isPlaying ? <span className="legacySelfStudyConversationPlaying" /> : null}
      </LmsButton>
      <span className="legacySelfStudyConversationContent">
        <span
          className="legacySelfStudyConversationTextBox"
          onClick={togglePlayback}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') {
              return
            }
            event.preventDefault()
            togglePlayback()
          }}
          role="button"
          tabIndex={0}
        >
          <span className="legacySelfStudyConversationName">
            <span>{name}</span>
            {!isSpeakingTurn ? <Volume2 aria-hidden="true" /> : null}
          </span>
          <span className="legacySelfStudyConversationText">{renderText()}</span>
        </span>
      </span>
      <CachedLearningAudio
        onCanPlay={() => {
          if (active && !autoStartedRef.current) {
            autoStartedRef.current = true
            onBeforePlayback?.(turnIndex)
            void audioRef.current?.play().catch(() => {
              setIsPlaying(false)
            })
          }
        }}
        onEnded={() => {
          setIsPlaying(false)
          if (active) {
            window.setTimeout(() => {
              onAudioFinished?.()
            }, 500)
          }
        }}
        onPause={() => {
          setIsPlaying(false)
        }}
        onPlay={() => {
          setIsPlaying(true)
        }}
        ref={audioRef}
        src={conversation.audio}
      />
    </div>
  )
}


export function ConversationTemplate({
  continuePending = false,
  continuePendingLabel,
  item,
  locale,
  onContinue,
  onResult,
  sourceOwner,
}: {
  readonly continuePending?: boolean
  readonly continuePendingLabel?: ReactNode
  readonly item: LearningContentItem
  readonly locale: string
  readonly onContinue: (result: SelfStudyContinueResult) => void
  readonly onResult?: (result: Exclude<SelfStudyContinueResult, 'skipped'>) => void
  readonly sourceOwner: string
}) {
  const { t } = useTranslation('book')
  const conversation = (item.conversation ?? []).filter(
    (entry) => entry.status !== false,
  )
  const isFillMode = Boolean(item.enabledFill)
  const isSpeakMode = Boolean(item.enabledSpeak)
  const fillSlots = useMemo<ConversationFillSlot[]>(() => {
    const slots = conversation.flatMap((turn, conversationIndex) => {
      const words = getConversationText(turn).split(' ')
      return (turn.fills ?? []).flatMap((wordIndex) => {
        const expected = words[wordIndex]
        if (!expected) {
          return []
        }

        return [{
          conversationIndex,
          expected,
          key: `${conversationIndex}-${wordIndex}`,
          sort: 0,
          wordIndex,
        }]
      })
    })

    return slots.map((slot, index) => ({ ...slot, sort: index + 1 }))
  }, [conversation])
  const [playIndex, setPlayIndex] = useState(0)
  const [isAutoPlaying, setIsAutoPlaying] = useState(true)
  const [showAll, setShowAll] = useState(false)
  const [showTranslation, setShowTranslation] = useState(false)
  const [roleRight, setRoleRight] = useState(true)
  const [fillAnswers, setFillAnswers] = useState<Record<string, string>>({})
  const [selectedFillChoiceIds, setSelectedFillChoiceIds] = useState<Record<string, string>>({})
  const [availableFillChoices, setAvailableFillChoices] = useState<ConversationFillChoice[]>(() =>
    shuffleItems(fillSlots.map((slot) => ({ id: slot.key, text: slot.expected }))),
  )
  const [fillResult, setFillResult] = useState<'correct' | 'incorrect' | null>(null)
  const [isSpeakRecording, setIsSpeakRecording] = useState(false)
  const conversationThreadRef = useRef<HTMLDivElement | null>(null)
  const recorder = useAudioRecorder({ maxDurationMs: 15000 })
  const scrollConversationTurnIntoView = useCallback((turnIndex: number) => {
    const thread = conversationThreadRef.current
    const turn = thread?.querySelector<HTMLElement>(`#conversationBox-${turnIndex}`)

    turn?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'nearest',
    })
  }, [])

  const progress = Math.round(((Math.min(playIndex, conversation.length - 1) + 1) / conversation.length) * 100)
  const currentSpeakTurn = conversation[playIndex]
  const currentTurnIsLearner = Boolean(currentSpeakTurn?.isRight) === roleRight
  const hasRecorderRecovery = Boolean(recorder.errorMessage)
  const visibleTurns = isFillMode
    ? conversation.map((turn, index) => ({ index, turn }))
    : conversation
        .slice(0, showAll ? conversation.length : playIndex + 1)
        .map((turn, index) => ({ index, turn }))

  const advancePlayback = () => {
    if (playIndex < conversation.length - 1) {
      setPlayIndex((previous) => previous + 1)
      return
    }

    setIsAutoPlaying(false)
    setShowAll(true)
  }

  const placeFillChoice = useCallback((slot: ConversationFillSlot, choice: ConversationFillChoice) => {
    if (fillResult) {
      return
    }

    const previousAnswer = fillAnswers[slot.key]
    const previousChoiceId = selectedFillChoiceIds[slot.key]
    const nextAnswers = { ...fillAnswers, [slot.key]: choice.text }

    setFillAnswers(nextAnswers)
    setSelectedFillChoiceIds((previous) => ({ ...previous, [slot.key]: choice.id }))
    setAvailableFillChoices((previous) => {
      const nextChoices = previous.filter((candidate) => candidate.id !== choice.id)
      if (previousAnswer) {
        nextChoices.push({ id: previousChoiceId ?? slot.key, text: previousAnswer })
      }
      return nextChoices
    })

    if (Object.keys(nextAnswers).length === fillSlots.length) {
      const nextResult =
        fillSlots.every((candidate) => nextAnswers[candidate.key] === candidate.expected)
          ? 'correct'
          : 'incorrect'
      setFillResult(nextResult)
      onResult?.(nextResult)
    }
  }, [fillAnswers, fillResult, fillSlots, onResult, selectedFillChoiceIds])

  const chooseFill = useCallback((choice: ConversationFillChoice) => {
    const slot = fillSlots.find((candidate) => !fillAnswers[candidate.key])
    if (!slot) {
      return
    }

    placeFillChoice(slot, choice)
  }, [fillAnswers, fillSlots, placeFillChoice])

  const dropFillChoice = useCallback((slot: ConversationFillSlot, choiceId: string) => {
    const choice = availableFillChoices.find((candidate) => candidate.id === choiceId)
    if (!choice) {
      return
    }

    placeFillChoice(slot, choice)
  }, [availableFillChoices, placeFillChoice])

  const reselectFill = useCallback((slot: ConversationFillSlot) => {
    if (fillResult || !fillAnswers[slot.key]) {
      return
    }

    const text = fillAnswers[slot.key]
    const id = selectedFillChoiceIds[slot.key] ?? slot.key
    setAvailableFillChoices((previous) => [...previous, { id, text }])
    setSelectedFillChoiceIds((previous) => {
      const nextChoiceIds = { ...previous }
      delete nextChoiceIds[slot.key]
      return nextChoiceIds
    })
    setFillAnswers((previous) => {
      const nextAnswers = { ...previous }
      delete nextAnswers[slot.key]
      return nextAnswers
    })
  }, [fillAnswers, fillResult, selectedFillChoiceIds])

  const handleFillKeyboardShortcut = useCallback((event: KeyboardEvent) => {
    if (
      !isFillMode ||
      fillResult ||
      event.altKey ||
      event.ctrlKey ||
      event.metaKey ||
      event.repeat ||
      !/^[1-9]$/.test(event.key)
    ) {
      return
    }

    const target = event.target
    if (
      target instanceof HTMLElement &&
      (target.isContentEditable || target.closest('input, textarea, select, [contenteditable="true"]'))
    ) {
      return
    }

    const choice = availableFillChoices[Number(event.key) - 1]
    if (!choice) {
      return
    }

    event.preventDefault()
    chooseFill(choice)
  }, [availableFillChoices, chooseFill, fillResult, isFillMode])

  useEffect(() => {
    window.addEventListener('keydown', handleFillKeyboardShortcut)

    return () => {
      window.removeEventListener('keydown', handleFillKeyboardShortcut)
    }
  }, [handleFillKeyboardShortcut])

  if (conversation.length === 0) {
    return (
      <div className="selfStudyStepCard selfStudyWarning" role="alert">
        This conversation step has no published turns yet.
      </div>
    )
  }

  const stopSpeaking = () => {
    recorder.stopRecording()
    setIsSpeakRecording(false)
    window.setTimeout(() => {
      advancePlayback()
    }, 150)
  }

  const skipUnavailableSpeaking = () => {
    recorder.stopRecording()
    setIsSpeakRecording(false)
    onContinue('skipped')
  }

  const handleStartSpeaking = async () => {
    setIsSpeakRecording(await recorder.startRecording())
  }

  return (
    <div
      className="legacySelfStudyConversation"
      data-legacy-owner={sourceOwner}
      data-legacy-template="11"
      data-qa="legacy-template-11-conversation"
    >
      {!isFillMode ? (
        <div className="legacySelfStudyConversationPlayback">
          <div className="boxWrapQuestion legacySelfStudyConversationPlaybackInner">
            {showAll ? (
              <div className="legacySelfStudyConversationActions">
                {isSpeakMode ? (
                  <LmsButton
                    className="legacySelfStudyConversationRoleButton"
                    onClick={() => {
                      setRoleRight((previous) => !previous)
                      setPlayIndex(0)
                      setShowAll(false)
                      setShowTranslation(false)
                      setIsAutoPlaying(true)
                    }}
                    type="button"
                    variant="outline"
                  >
                    {t('Switch role')}
                  </LmsButton>
                ) : null}
                <LmsButton
                  className="legacySelfStudyConversationTranslationButton"
                  onClick={() => {
                    setShowTranslation((previous) => !previous)
                  }}
                  size="sm"
                  variant="ghost"
                >
                  {t(showTranslation ? 'Hide translation' : 'See translation')}
                </LmsButton>
              </div>
            ) : (
              <LmsButton
                aria-label={isAutoPlaying ? 'Pause conversation' : 'Play conversation'}
                className="legacySelfStudyConversationPlaybackButton"
                onClick={() => {
                  if (isAutoPlaying) {
                    setIsAutoPlaying(false)
                    return
                  }
                  setShowTranslation(false)
                  setIsAutoPlaying(true)
                }}
                size="icon-lg"
                type="button"
                variant="accent"
              >
                {isAutoPlaying ? <Pause aria-hidden="true" /> : <Play aria-hidden="true" />}
                <svg
                  aria-hidden="true"
                  className="legacySelfStudyConversationPlaybackProgress"
                  viewBox="0 0 56 56"
                >
                  <circle className="legacySelfStudyConversationPlaybackProgressBase" cx="28" cy="28" r="25" />
                  <circle
                    className="legacySelfStudyConversationPlaybackProgressActive"
                    cx="28"
                    cy="28"
                    r="25"
                    style={{
                      '--legacy-self-study-conversation-progress': `${157.08 - (157.08 * progress) / 100}`,
                    } as CSSProperties}
                  />
                </svg>
              </LmsButton>
            )}
          </div>
        </div>
      ) : null}

      <div
        className="boxWrapQuestion legacySelfStudyConversationThread"
        id="conversationBox"
        ref={conversationThreadRef}
      >
        {visibleTurns.map(({ index, turn }) => {
          const isLearnerTurn = isSpeakMode && Boolean(turn.isRight) === roleRight
          return (
            <LegacyConversationTurn
              active={
                !isFillMode && !showAll && index === playIndex && isAutoPlaying && !isLearnerTurn
              }
              conversation={turn}
              fillAnswers={fillAnswers}
              fillResult={fillResult}
              fillSlots={fillSlots.filter((slot) => slot.conversationIndex === index)}
              isSpeakingTurn={isLearnerTurn && index === playIndex}
              key={`${item.id}-conversation-${turn.id ?? index}`}
              locale={locale}
              onAudioFinished={advancePlayback}
              onBeforePlayback={scrollConversationTurnIntoView}
              onFillDrop={dropFillChoice}
              onFillReselect={reselectFill}
              showTranslation={showTranslation}
              turnIndex={index}
            />
          )
        })}
      </div>

      {isSpeakMode && !showAll && currentTurnIsLearner ? (
        <div className="legacySelfStudyConversationSpeakBar">
          <LegacySpeechRecoveryNotice
            message={recorder.errorMessage}
            onRetry={() => void handleStartSpeaking()}
            onSkip={skipUnavailableSpeaking}
          />
          {!hasRecorderRecovery ? (
            <LmsButton
              aria-label={isSpeakRecording ? 'Stop recording' : 'Record your answer'}
              className={`legacySelfStudyConversationRecordButton${isSpeakRecording ? ' recording' : ''}`}
              onClick={() => {
                if (isSpeakRecording) {
                  stopSpeaking()
                  return
                }
                void handleStartSpeaking()
              }}
              size="icon-lg"
              type="button"
              variant="accent"
            >
              {isSpeakRecording ? <Square aria-hidden="true" /> : <Mic aria-hidden="true" />}
            </LmsButton>
          ) : null}
        </div>
      ) : null}

      {isFillMode && !fillResult ? (
        <div className="legacySelfStudyConversationFillBar">
          <div className="legacySelfStudyConversationFillChoices">
            {availableFillChoices.map((choice, index) => (
              <LmsButton
                aria-keyshortcuts={index < 9 ? String(index + 1) : undefined}
                className="legacySelfStudyConversationFillChoice"
                draggable
                key={choice.id}
                onClick={() => chooseFill(choice)}
                onDragStart={(event) => {
                  event.dataTransfer.effectAllowed = 'move'
                  event.dataTransfer.setData('application/x-hanaspeak-fill-choice', choice.id)
                  event.dataTransfer.setData('text/plain', `conversation-fill-choice:${choice.id}`)
                }}
                type="button"
                variant="outline"
              >
                {choice.text}
                <span className="legacySelfStudyConversationSort">{index + 1}</span>
              </LmsButton>
            ))}
          </div>
        </div>
      ) : null}

      {((!isFillMode && showAll && !isSpeakMode) || fillResult || (isSpeakMode && showAll)) ? (
        <LegacyTemplateResultBar
          continuePending={continuePending}
          continuePendingLabel={continuePendingLabel}
          finished={false}
          onNext={() => {
            onContinue(fillResult ?? 'correct')
          }}
        />
      ) : null}
    </div>
  )
}

